import type { BorrowTermSheet, SupplyTermSheet, TermSheet } from './types'

/**
 * Severity model — PURE, derived only from structured fields.
 *
 * Space is the binding constraint at every display depth, so ranking has to be
 * principled rather than per-lender taste. There is deliberately NO
 * hand-maintained list of "scary markets": a newly integrated lender is
 * classified correctly the moment its adapter sets the right fields.
 *
 * - `critical` — you can lose MORE than the amount at stake, or lose it
 *   without doing anything wrong. This is the only tier that should gate a
 *   signature.
 * - `warn` — it costs money, or blocks you.
 * - `info` — everything else.
 */
export type Severity = 'critical' | 'warn' | 'info'

export interface SeverityFinding {
  severity: Severity
  /** Stable slug — the join key for UI copy and for tests. */
  id: string
  /** Ready-to-render sentence. */
  message: string
  side: 'supply' | 'borrow' | 'market'
}

const SEVERITY_ORDER: Record<Severity, number> = {
  critical: 0,
  warn: 1,
  info: 2,
}

/** Sort findings most-severe-first, stable within a tier. */
export function rankFindings(findings: SeverityFinding[]): SeverityFinding[] {
  return [...findings].sort(
    (a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity],
  )
}

function fmtSecs(secs: number): string {
  if (secs >= 86_400) {
    const d = secs / 86_400
    return `${d % 1 === 0 ? d : d.toFixed(1)} day${d === 1 ? '' : 's'}`
  }
  if (secs >= 3_600) {
    const h = secs / 3_600
    return `${h % 1 === 0 ? h : h.toFixed(1)} hour${h === 1 ? '' : 's'}`
  }
  if (secs >= 60) {
    const m = Math.round(secs / 60)
    return `${m} minute${m === 1 ? '' : 's'}`
  }
  return `${secs} second${secs === 1 ? '' : 's'}`
}

function marketFindings(sheet: TermSheet): SeverityFinding[] {
  const out: SeverityFinding[] = []
  const { governance: g, oracle: o, utilization: u } = sheet

  if (o?.band === 'CRITICAL') {
    out.push({
      severity: 'critical',
      id: 'oracle-critical',
      side: 'market',
      message: `The price oracle for this market scores CRITICAL for feed correctness${
        o.priceDescription ? ` (reports ${o.priceDescription})` : ''
      }.`,
    })
  } else if (o?.band === 'HIGH') {
    out.push({
      severity: 'warn',
      id: 'oracle-high',
      side: 'market',
      message: 'The price oracle for this market scores HIGH risk.',
    })
  }
  if (o?.flags?.includes('wrong-asset')) {
    out.push({
      severity: 'critical',
      id: 'oracle-wrong-asset',
      side: 'market',
      message: `The oracle does not price the intended asset${
        o.intendedPair ? ` (expected ${o.intendedPair})` : ''
      }.`,
    })
  }
  if (o?.mutability?.mutable && !o.mutability.timelockSecs) {
    out.push({
      severity: 'warn',
      id: 'oracle-swappable',
      side: 'market',
      message:
        'The price oracle can be replaced with no delay by its controller.',
    })
  }

  if (g?.mutability === 'governed') {
    if (g.controllerKind === 'EOA') {
      out.push({
        severity: 'critical',
        id: 'governance-eoa',
        side: 'market',
        message:
          'A single private key (an EOA, not a multisig or timelock) can change this market’s parameters.',
      })
    } else if (g.controllerKind === 'TIMELOCK' && g.timelockUnknown) {
      // Do not raise a false alarm on the safest shape we screen for.
      out.push({
        severity: 'info',
        id: 'governance-timelock-unknown',
        side: 'market',
        message:
          'Parameter changes go through a timelock, but its delay could not be read.',
      })
    } else if (!g.timelockSecs) {
      out.push({
        severity: 'warn',
        id: 'governance-no-timelock',
        side: 'market',
        message: `Parameters can be changed with no notice period${
          g.controllerKind ? ` by a ${g.controllerKind.toLowerCase()}` : ''
        }.`,
      })
    }
  }

  if (u && u.utilization >= 0.98) {
    out.push({
      severity: 'warn',
      id: 'utilization-full',
      side: 'market',
      message: `Utilization is ${(u.utilization * 100).toFixed(1)} % — withdrawals may not be possible until borrowers repay.`,
    })
  }

  return out
}

export function supplyFindings(supply: SupplyTermSheet): SeverityFinding[] {
  const out: SeverityFinding[] = []

  if (supply.principal.risks.includes('first-loss')) {
    out.push({
      severity: 'critical',
      id: 'first-loss',
      side: 'supply',
      message:
        'This is a junior/first-loss position — it absorbs losses before other depositors and can be marked down sharply.',
    })
  }
  if (supply.principal.risks.includes('physical-delivery')) {
    out.push({
      severity: 'critical',
      id: 'physical-delivery',
      side: 'supply',
      message:
        'On default you can be settled in collateral rather than the asset you lent.',
    })
  }
  if (supply.counterparty.socializedLoss) {
    out.push({
      severity: 'warn',
      id: 'socialized-loss',
      side: 'supply',
      message:
        'Bad debt is socialised across all lenders in this market, not borne by the defaulting borrower alone.',
    })
  }
  if (supply.counterparty.solvency === 'undercollateralized') {
    out.push({
      severity: 'critical',
      id: 'undercollateralized',
      side: 'supply',
      message:
        'Borrowers here are not fully collateralised on-chain — repayment depends on off-chain credit.',
    })
  }
  if (supply.counterparty.solvency === 'nav-attested') {
    out.push({
      severity: 'warn',
      id: 'nav-attested',
      side: 'supply',
      message:
        'The share price is published by an operator; there is no on-chain solvency invariant.',
    })
  }

  if (supply.exit.priceRisk === 'may-be-impossible') {
    out.push({
      severity: 'critical',
      id: 'exit-may-be-impossible',
      side: 'supply',
      message:
        'Exiting early means selling on a market that can be empty — there may be no exit before maturity at any price.',
    })
  } else if (supply.exit.priceRisk === 'market-price') {
    out.push({
      severity: 'warn',
      id: 'exit-market-price',
      side: 'supply',
      message:
        'Exiting early means selling at the prevailing market price, which can be below par.',
    })
  }

  if (supply.exit.cooldownSecs) {
    out.push({
      severity: 'warn',
      id: 'exit-cooldown',
      side: 'supply',
      message: `Withdrawals require a ${fmtSecs(supply.exit.cooldownSecs)} waiting period.`,
    })
  } else if (
    supply.exit.settlement === 'async' &&
    supply.exit.mode !== 'instant'
  ) {
    out.push({
      severity: 'warn',
      id: 'exit-async',
      side: 'supply',
      message: 'Withdrawals are not instant — they queue or need a request.',
    })
  }

  const exitFee = supply.exit.fees.find(
    (f) => f.when === 'exit' && f.value > 0 && !f.indicative,
  )
  if (exitFee) {
    out.push({
      severity: 'warn',
      id: 'exit-fee',
      side: 'supply',
      message: `Exiting costs ${exitFee.label.toLowerCase()}.`,
    })
  }

  if (
    supply.exit.liquidity &&
    supply.exit.liquidity.assets <= 0 &&
    supply.exit.settlement === 'sync'
  ) {
    out.push({
      severity: 'warn',
      id: 'no-liquidity',
      side: 'supply',
      message:
        'There is no withdrawable liquidity right now — your deposit cannot be taken out until borrowers repay.',
    })
  }

  if (supply.backedBy) {
    if ((supply.backedBy.worstRiskScore ?? 0) >= 4) {
      out.push({
        severity: 'warn',
        id: 'backing-quality',
        side: 'supply',
        message:
          'Some collateral backing this market is low quality or thinly traded.',
      })
    }
    if (
      supply.backedBy.worstOracleBand === 'HIGH' ||
      supply.backedBy.worstOracleBand === 'CRITICAL'
    ) {
      out.push({
        severity: 'warn',
        id: 'backing-oracle',
        side: 'supply',
        message:
          'Collateral backing this market is priced by an oracle flagged as risky.',
      })
    }
  }

  const points = supply.rate.rewards?.filter((r) => r.indicative) ?? []
  if (points.length > 0) {
    out.push({
      severity: 'info',
      id: 'points-rewards',
      side: 'supply',
      message:
        'Part of the advertised return is a points program with no priceable value.',
    })
  }

  if (!supply.availability.canOpen) {
    out.push({
      severity: 'warn',
      id: 'supply-closed',
      side: 'supply',
      message: `Deposits are currently unavailable${
        supply.availability.blockedBy
          ? ` (${supply.availability.blockedBy.replace(/-/g, ' ')})`
          : ''
      }.`,
    })
  }

  return out
}

export function borrowFindings(borrow: BorrowTermSheet): SeverityFinding[] {
  const out: SeverityFinding[] = []
  const liq = borrow.liquidation

  // A repay LOCK. Critical rather than warning, and the reason is the second
  // clause: every other lender we carry lets a borrower defend a position by
  // repaying, so a UI that shows a health factor implicitly promises that
  // lever exists. Here it does not for the length of the window — and the
  // window is armed by the very actions a borrower takes to defend themselves
  // (posting collateral) as well as by borrowing.
  if (borrow.exit.cooldownSecs) {
    out.push({
      severity: 'critical',
      id: 'repay-locked',
      side: 'borrow',
      message: `The debt CANNOT be repaid for ${fmtSecs(
        borrow.exit.cooldownSecs,
      )} after opening or topping up: the position cannot be closed, deleveraged or migrated out in that window, and an incoming liquidation cannot be averted by repaying (adding collateral still works).`,
    })
  }

  if (liq.seizure === 'full-collateral') {
    out.push({
      severity: 'critical',
      id: 'full-collateral-seizure',
      side: 'borrow',
      message:
        'On default a liquidator takes your ENTIRE collateral, not just the amount owed — at 50 % LTV that is roughly twice the borrowed value.',
    })
  }
  if (liq.trigger === 'time' || liq.trigger === 'price-and-time') {
    out.push({
      severity: 'critical',
      id: 'time-liquidation',
      side: 'borrow',
      message: `This loan can be liquidated for being LATE, regardless of how over-collateralised it is${
        borrow.maturity.graceSecs
          ? ` — the grace period is ${fmtSecs(borrow.maturity.graceSecs)}`
          : ''
      }. A healthy health factor does not protect you.`,
    })
  }
  if (liq.redeemable || liq.trigger === 'redemption') {
    // Build the sentence from the MECHANICS. "Your collateral can be redeemed"
    // states an effect that reads as a governance power or a bug; what a
    // borrower needs is who can do it, why they would, who gets hit, and
    // whether there is anything to do about it.
    const r = liq.redemption
    const who =
      r?.trigger === 'permissionless-arbitrage'
        ? 'Anyone holding the borrowed token can redeem it for collateral at any time'
        : 'Your collateral can be taken while the position is healthy'
    const why =
      r?.driver === 'below-peg'
        ? ' — this is the peg-defence mechanism, not a liquidation and not a governance decision, and it only pays them while the token trades below its target'
        : ''
    const whom =
      r?.order === 'lowest-rate-first'
        ? '. Redemptions take the LOWEST-interest-rate positions first, so your rate is your place in the queue'
        : r?.order === 'pro-rata'
          ? '. Collateral is skimmed pro-rata from EVERY borrower in this market, so there is no queue to out-run'
          : ''
    const impact =
      r?.valueImpact === 'usd-neutral'
        ? '. Your debt is cancelled against the collateral taken, so you end up roughly USD-neutral but lose collateral exposure'
        : ''
    const fix = r?.defence ? `. ${r.defence}` : ''
    out.push({
      severity: 'critical',
      id: 'redeemable',
      side: 'borrow',
      message: `${who}${why}${whom}${impact}${fix}`.replace(/\.\s*\./g, '.'),
    })
  }
  if (borrow.maturity.atMaturity === 'physical-delivery') {
    out.push({
      severity: 'critical',
      id: 'physical-delivery',
      side: 'borrow',
      message:
        'After maturity plus the liquidation window, unpaid collateral is delivered to lenders.',
    })
  }
  if (borrow.maturity.atMaturity === 'default-seizure') {
    out.push({
      severity: 'critical',
      id: 'default-seizure',
      side: 'borrow',
      message: `Missing the payment window puts the loan in default and forfeits the collateral${
        borrow.maturity.graceSecs
          ? ` — the window is only ${fmtSecs(borrow.maturity.graceSecs)}`
          : ''
      }.`,
    })
  }
  if (borrow.maturity.atMaturity === 'liquidatable') {
    out.push({
      severity: 'critical',
      id: 'past-due-liquidatable',
      side: 'borrow',
      message:
        'Once maturity passes the loan is in default and can be liquidated regardless of its health or LTV.',
    })
  }

  const latePenalty = borrow.fees.find((f) => f.when === 'late' && f.value > 0)
  if (latePenalty) {
    out.push({
      severity: 'warn',
      id: 'late-penalty',
      side: 'borrow',
      message: `Repaying late accrues ${latePenalty.label.toLowerCase()}.`,
    })
  }

  if (borrow.exit.earlyRepay === 'penalty') {
    out.push({
      severity: 'warn',
      id: 'early-repay-penalty',
      side: 'borrow',
      message: 'Repaying before maturity costs a penalty.',
    })
  }
  if (borrow.exit.earlyRepay === 'not-allowed') {
    out.push({
      severity: 'warn',
      id: 'no-early-repay',
      side: 'borrow',
      message: 'This loan cannot be repaid before maturity.',
    })
  }
  if (
    borrow.debtShape === 'static-face' &&
    borrow.exit.atMaturityCost === 'face'
  ) {
    out.push({
      severity: 'info',
      id: 'static-debt',
      side: 'borrow',
      message:
        'The amount owed is fixed at trade time and does not accrue — repaying early does not reduce it.',
    })
  }
  if (borrow.exit.overRepayReverts) {
    out.push({
      severity: 'warn',
      id: 'over-repay-reverts',
      side: 'borrow',
      message:
        'Sending more than the exact debt REVERTS — the repayment must be sized precisely.',
    })
  }
  if (borrow.debtShape === 'prepaid') {
    out.push({
      severity: 'warn',
      id: 'prepaid-interest',
      side: 'borrow',
      message:
        'Interest is prepaid in a separate token; running out triggers a forced top-up at a punitive rate and freezes withdrawals.',
    })
  }

  const origination = borrow.fees.find((f) => f.when === 'entry' && f.value > 0)
  if (origination) {
    out.push({
      severity: 'info',
      id: 'origination-fee',
      side: 'borrow',
      message: `An upfront ${origination.label.toLowerCase()} is charged at borrow time.`,
    })
  }

  if (borrow.acceptedCollateral) {
    if (
      borrow.acceptedCollateral.worstOracleBand === 'HIGH' ||
      borrow.acceptedCollateral.worstOracleBand === 'CRITICAL'
    ) {
      out.push({
        severity: 'warn',
        id: 'collateral-oracle',
        side: 'borrow',
        message:
          'Collateral you can post here is priced by an oracle flagged as risky — a mispricing can liquidate you.',
      })
    }
  }

  if (!borrow.availability.canOpen) {
    out.push({
      severity: 'warn',
      id: 'borrow-closed',
      side: 'borrow',
      message: `Borrowing is currently unavailable${
        borrow.availability.blockedBy
          ? ` (${borrow.availability.blockedBy.replace(/-/g, ' ')})`
          : ''
      }.`,
    })
  }
  if (borrow.availability.requires?.includes('caller-allowlist')) {
    out.push({
      severity: 'warn',
      id: 'caller-allowlist',
      side: 'borrow',
      message:
        'Only governance-approved contracts may borrow here, which blocks leverage and other routed flows.',
    })
  }

  return out
}

/**
 * All findings for one side of a sheet, ranked most-severe-first. Pass
 * `side: 'supply' | 'borrow'` — market-level findings are always included
 * because governance and oracle affect both sides.
 */
export function findingsFor(
  sheet: TermSheet,
  side: 'supply' | 'borrow',
): SeverityFinding[] {
  const sideFindings =
    side === 'supply'
      ? sheet.supply
        ? supplyFindings(sheet.supply)
        : []
      : sheet.borrow
        ? borrowFindings(sheet.borrow)
        : []
  return rankFindings([...sideFindings, ...marketFindings(sheet)])
}

/** Does this side carry anything that should gate a signature? */
export function hasCritical(
  sheet: TermSheet,
  side: 'supply' | 'borrow',
): boolean {
  return findingsFor(sheet, side).some((f) => f.severity === 'critical')
}
